import { useState } from "react";
import { Sheet } from "../../components/Sheet";
import { Icon } from "../../components/Icon";
import { Spinner } from "../../components/ui";
import { useArtists, useCreateArtist } from "../../api/queries";
import { haptic } from "../../telegram/haptics";
import { messagesOf } from "../../api/errors";
import { useT } from "../../i18n/strings";
import { fieldStyles as f } from "../../components/fields";

interface ArtistPickerProps {
  value: string | null;
  valueName: string;
  onChange: (id: string | null, name: string) => void;
}

/**
 * Select or create, in one control.
 *
 * The API's POST /artists/ is create-or-select on the normalized name — it
 * answers 200 for an existing artist and 201 for a new one, with the same body
 * either way — so typing a name that already exists cannot fork a second row.
 */
export function ArtistPicker({ value, valueName, onChange }: ArtistPickerProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const artists = useArtists(term.trim());
  const create = useCreateArtist();

  const query = term.trim();
  const exactExists = artists.data?.some(
    (artist) => artist.name.toLocaleLowerCase() === query.toLocaleLowerCase(),
  );

  const choose = (id: string | null, name: string) => {
    onChange(id, name);
    setOpen(false);
    setTerm("");
  };

  const createAndChoose = async () => {
    if (!query || create.isPending) return;
    setError(null);
    try {
      const artist = await create.mutateAsync(query);
      haptic.success();
      choose(artist.id, artist.name);
    } catch (caught) {
      setError(messagesOf(caught)[0] ?? t.errors.title);
      haptic.error();
    }
  };

  return (
    <>
      <button
        type="button"
        className={f.picker}
        aria-label={t.form.artistPlaceholder}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span
          className={[f.pickerValue, !value && f.pickerEmpty, "bidi"].filter(Boolean).join(" ")}
        >
          {value ? valueName : t.form.artistPlaceholder}
        </span>
        <Icon name="chevronDown" size={20} className={f.pickerChevron} />
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} title={t.form.artistPlaceholder}>
        <div className={f.searchBox} style={{ marginBlockEnd: "var(--space-3)" }}>
          <Icon name="search" size={20} />
          <input
            autoFocus
            dir="auto"
            className={f.searchInput}
            value={term}
            placeholder={t.form.artistSearchPlaceholder}
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>

        <div className={f.options} role="listbox">
          {value && (
            <button
              type="button"
              className={f.option}
              onClick={() => {
                haptic.select();
                choose(null, "");
              }}
            >
              <span className={f.optionLabels}>
                <span className={f.optionFa}>{t.form.none}</span>
              </span>
            </button>
          )}

          {artists.isPending ? (
            <div style={{ display: "grid", placeItems: "center", padding: "var(--space-4)" }}>
              <Spinner />
            </div>
          ) : (
            artists.data?.map((artist) => (
              <button
                key={artist.id}
                type="button"
                role="option"
                aria-selected={artist.id === value}
                className={[f.option, artist.id === value && f.optionSelected]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  haptic.select();
                  choose(artist.id, artist.name);
                }}
              >
                <span className={f.optionLabels}>
                  <span className={`${f.optionFa} bidi`}>{artist.name}</span>
                </span>
                <span className="mono">{artist.artwork_count}</span>
                {artist.id === value && <Icon name="check" size={20} className={f.optionCheck} />}
              </button>
            ))
          )}

          {!artists.isPending && !artists.data?.length && !query && (
            <p className={f.hint}>{t.form.noArtists}</p>
          )}
        </div>

        {query && !exactExists && (
          <button
            type="button"
            className={f.addTrigger}
            onClick={createAndChoose}
            disabled={create.isPending}
          >
            {create.isPending ? <Spinner size={20} /> : <Icon name="add" size={20} />}
            <span className="bidi">{t.form.artistCreate(query)}</span>
          </button>
        )}
        {error && <p className={f.error}>{error}</p>}
      </Sheet>
    </>
  );
}
